import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"
import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { motion } from "framer-motion"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-[var(--hp-radius-md)] text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hp-color-focus)] disabled:pointer-events-none disabled:opacity-50 shadow-hp-sm",
  {
    variants: {
      variant: {
        default: "bg-[var(--hp-color-primary)] text-[var(--hp-color-on-primary)] hover:bg-[var(--hp-color-primary-hover)] border border-transparent",
        primary: "bg-[var(--hp-color-accent)] text-[var(--hp-color-on-accent)] hover:bg-[var(--hp-color-accent-hover)] border border-transparent",
        destructive: "bg-[var(--hp-danger-600)] text-[var(--hp-white)] hover:bg-[var(--hp-danger-700)] border border-transparent",
        outline: "border border-[var(--hp-color-border-strong)] bg-[var(--hp-color-surface)] text-[var(--hp-color-primary)] hover:bg-[var(--hp-color-surface-muted)]",
        secondary: "bg-[var(--hp-color-surface)] text-[var(--hp-color-primary)] border border-[var(--hp-color-border-strong)] hover:bg-[var(--hp-color-surface-muted)]",
        ghost: "hover:bg-[var(--hp-color-surface-muted)] text-[var(--hp-color-primary)] shadow-none border border-transparent",
        link: "text-[var(--hp-color-accent-text)] underline-offset-4 hover:underline shadow-none border border-transparent bg-transparent",
        accent: "bg-[var(--hp-color-accent)] text-[var(--hp-color-on-accent)] hover:bg-[var(--hp-color-accent-hover)] border border-transparent",
      },
      size: {
        default: "min-h-[2.75rem] px-5 py-[0.7rem]", // Hipparchus standard
        sm: "h-9 rounded-[var(--hp-radius-sm)] px-3 text-xs",
        lg: "h-12 rounded-[var(--hp-radius-lg)] px-8 text-lg",
        icon: "h-10 w-10 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : motion.button
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      whileHover={!asChild ? { scale: 1.02 } : undefined}
      whileTap={!asChild ? { scale: 0.97 } : undefined}
      {...props}
    />
  )
})
Button.displayName = "Button"

export { Button, buttonVariants }
export default Button;
